
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { Button } from "@/components/fun-ui/Button";
import { TextareaWithMaxLength } from "@/components/fun-ui/TextareaWithMaxLength";
import { HTMLAttributes, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fun } from "@/lib/request";
import { toast } from "react-hot-toast";
import { api } from "@/lib/env";
import { FeedbackScene, type Feedback as FeedbackConfig } from "@/stores/slices/commonSlice";
import { useFeedback } from "@/stores/appStore";
import { cn, transferCamelToSnake } from "@/lib/utils";
import { Checkbox } from "../ui/checkbox";
import { AlertCircleIcon, WechatIcon } from "../fun-ui/icons";
import { Tip } from "../fun-ui/Tip";
import ReactMarkdown from "react-markdown";
import {
  buildFeedbackRequestPayload,
  isRequiredFeedbackContentMissing,
  mergePreparedFeedbackContent,
  type FeedbackContentTransformer,
} from "@/lib/feedback";

const feedbackTitleFallbacks: Partial<Record<FeedbackScene, string>> = {
  summaryFigure: "本次综述插图效果如何？",
};

type FeedbackPresentation = {
  title?: ReactNode;
  description?: ReactNode;
  fieldKeys?: string[];
  requireContent?: boolean;
  contentLabel?: string;
  contentPlaceholder?: string;
  cancelLabel?: string;
  submitLabel?: string;
  loadingMessage?: string;
  successMessage?: string;
  failureMessage?: string;
};

function isSameFeedbackContent(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  return aKeys.length === bKeys.length
    && aKeys.every((key) => Object.is(a[key], b[key]));
}

export const Feedback = ({
  scene = 'default',
  children = '',
  sceneContents,
  autoOpenKey,
  onBeforeOpen,
  onOpenStateChange,
  prepareSceneContents,
  transformSubmitContent,
  presentation,
}: {
  scene: FeedbackScene;
  children: string | React.JSX.Element;
  // 可以补充一些额外的提交参数
  sceneContents?: Record<string, any>;
  autoOpenKey?: string | number | null;
  onBeforeOpen?: () => boolean | Promise<boolean>;
  onOpenStateChange?: (open: boolean) => void;
  prepareSceneContents?: () => Record<string, unknown> | Promise<Record<string, unknown>>;
  transformSubmitContent?: FeedbackContentTransformer;
  presentation?: FeedbackPresentation;
}) => {
  const {
    value: feedbackConfig,
    fetch
  } = useFeedback();

  const defaultContent = useMemo(() => ({
    ...sceneContents,
    score: 0,
    suggestions: '',
    content: '',
    contact: false,
  }), [sceneContents]);

  const [content, setContent] = useState(defaultContent);
  const contentRef = useRef(defaultContent);
  const [open, setOpen] = useState(false);
  const lastAutoOpenKeyRef = useRef<string | number | null>(null);
  const [loading, setLoading] = useState(false);
  const updateOpen = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenStateChange?.(nextOpen);
  }, [onOpenStateChange]);
  const config = useMemo(() => {
    return presentation?.fieldKeys
      || feedbackConfig?.scene[scene]
      || feedbackConfig?.scene['default']
      || null;
  }, [feedbackConfig, presentation?.fieldKeys, scene]);
  const getContentKey = (configKey: string) => {
    return feedbackConfig?.[configKey]?.key || configKey;
  };

  const buttonDisabled = useMemo(() => {
    if (isRequiredFeedbackContentMissing(content, !!presentation?.requireContent)) {
      return true;
    }
    if (feedbackConfig && config?.length) {
      return !config.every((key) => {
        const itemConfig = feedbackConfig[key];
        const contentKey = itemConfig.key || key;
        const required = itemConfig.required;
        let able = !!(content as any)[contentKey];
        if (contentKey === 'suggestions') {
          able = content.score > 6 || !!content.suggestions.length;
        }
        return required ? able : true;
      });
    }
    return true;
  }, [content, config, feedbackConfig, presentation?.requireContent]);
  useEffect(() => {
    if (!open) {
      setContent((currentContent) => (
        isSameFeedbackContent(currentContent, defaultContent)
          ? currentContent
          : defaultContent
      ));
    }
  }, [defaultContent, open]);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  const handleOpenChange = useCallback(async (nextOpen: boolean) => {
    if (!nextOpen) {
      updateOpen(false);
      return;
    }
    if (onBeforeOpen) {
      const shouldOpen = await onBeforeOpen();
      if (!shouldOpen) {
        return;
      }
    }
    // Feedback is an opt-in dialog. Do not fetch its remote schema while the
    // page is only rendering the trigger; load it at the first open instead.
    if (!feedbackConfig) {
      // Keep the controlled Dialog open while the schema request is in flight.
      // Besides giving the trigger an immediate state transition, this tells
      // transient parents (for example the Writer figure hover toolbar) not to
      // unmount the trigger before the remote config arrives.
      updateOpen(true);
      await fetch();
    }
    if (prepareSceneContents) {
      try {
        const preparedContent = await prepareSceneContents();
        const nextContent = mergePreparedFeedbackContent(defaultContent, preparedContent);
        contentRef.current = nextContent;
        setContent(nextContent);
      } catch {
        contentRef.current = defaultContent;
        setContent(defaultContent);
      }
    }
    updateOpen(true);
  }, [defaultContent, fetch, feedbackConfig, onBeforeOpen, prepareSceneContents, updateOpen]);

  useEffect(() => {
    if (autoOpenKey == null) return;
    if (lastAutoOpenKeyRef.current === autoOpenKey) {
      return;
    }
    lastAutoOpenKeyRef.current = autoOpenKey;
    void handleOpenChange(true);
  }, [autoOpenKey, config, feedbackConfig, handleOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      void handleOpenChange(nextOpen);
    }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      {feedbackConfig && config ? <DialogContent
        className="w-[480px]"
        {...(!presentation?.description ? { "aria-describedby": undefined } : {})}
      >
        <DialogHeader>
          <DialogTitle>{presentation?.title || (typeof feedbackConfig.title === 'string'
            ? feedbackConfig.title
            : feedbackConfig.title[scene] || feedbackTitleFallbacks[scene] || feedbackConfig.title['default'])}
          </DialogTitle>
          {presentation?.description ? (
            <DialogDescription className="text-sm leading-6 text-muted-foreground">
              {presentation.description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="flex flex-col">
          {
            config.map((key) => {
              const configItemKey = getContentKey(key);
              if (configItemKey === 'score') {
                const o = feedbackConfig[key]
                return (
                  <div key={key}>
                    <div className="text-sm flex flex-row items-center">
                      {
                        o.required ? <span className="text-destructive">*</span> : null
                      }
                      <ReactMarkdown components={{
                        a: ({ children, ...props }: HTMLAttributes<HTMLAnchorElement>) => (
                          <a
                            target="_blank"
                            className={cn("text-primary hover:underline underline-offset-4 cursor-pointer")}
                            {...props}
                          >{children}</a>
                        ),
                      }}>{o.label}</ReactMarkdown>
                    </div>
                    <div className="flex flex-row gap-2 mt-4">
                      {
                        (o.options as FeedbackConfig['score']['options']).map((option) => {
                          return (
                            <div
                              key={option.score}
                              className="flex-1 flex border rounded-md py-2.5 gap-2 leading-none border-border text-muted-foreground cursor-pointer items-center justify-center  select-none"
                              style={content.score === option.score ? {
                                borderColor: option.style.borderColor,
                                backgroundColor: option.style.backgroundColor,
                                color: option.style.color,
                              } : {}}
                              onClick={() => {
                                setContent({
                                  ...contentRef.current,
                                  suggestions: '',
                                  score: option.score,
                                });
                              }}
                            >
                              <span>{option.emoji}</span>
                              <span>{option.label}</span>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )
              } else if (configItemKey === 'suggestions') {
                const o = feedbackConfig[key];
                const selectSuggestions = content.suggestions.split(',');
                const suggestions = (feedbackConfig.score.options.find((item) => {
                  return item.score === content.score;
                })?.suggestions || []);
                if (!suggestions.length) {
                  return null;
                }
                return (
                  <div key={key}>
                    <div className="mt-6 text-sm">{
                      o.required ? <span className="text-destructive">*</span> : null
                    }{o.label}</div>
                    <div className="flex flex-row flex-wrap gap-2 mt-4">
                      {
                        suggestions.map((suggestion) => {
                          return (
                            <div
                              key={suggestion}
                              className={cn(
                                "border border-border rounded-md py-1.5 px-3 text-muted-foreground cursor-pointer items-center justify-center text-xs select-none",
                                selectSuggestions.includes(suggestion) && "border-primary text-primary bg-primary/10"
                              )}
                              onClick={() => {
                                const ss = suggestions.filter((s) => {
                                  return suggestion === s ? !selectSuggestions.includes(s) : selectSuggestions.includes(s);
                                })
                                setContent({
                                  ...contentRef.current,
                                  suggestions: ss.join(','),
                                });
                              }}
                            >
                              {suggestion}
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                )
              } else if (configItemKey === 'content') {
                const o = feedbackConfig[key];
                return (
                  <div
                    key={key}
                    className="flex-1 w-full mt-5"
                  >
                    {presentation?.contentLabel ? (
                      <div className="mb-2 text-sm">
                        {presentation.requireContent ? <span className="text-destructive">*</span> : null}
                        {presentation.contentLabel}
                      </div>
                    ) : null}
                    <TextareaWithMaxLength
                      maxLength={o.maxLength}
                      placeholder={presentation?.contentPlaceholder || o.placeholder}
                      containerClassName="rounded-md border border-input bg-transparent"
                      className="min-h-[80px] resize-none outline-none ring-0 focus-visible:ring-0"
                      value={content.content}
                      onChange={(e) => {
                        setContent({
                          ...contentRef.current,
                          content: e.currentTarget.value,
                        });
                      }}
                    />
                  </div>
                );
              } else if (configItemKey === 'contact') {
                const o = feedbackConfig[key];
                return (
                  <label
                    key={key}
                    className="text-annotation-foreground flex flex-row items-center text-sm gap-2.5 mt-3.5 select-none cursor-pointer"
                  >
                    <Checkbox
                      checked={content.contact}
                      onCheckedChange={(value) => {
                        setContent({
                          ...contentRef.current,
                          contact: !!value,
                        })
                      }}
                      className="data-[state=unchecked]:border-annotation-foreground"
                    />
                    <div className="flex gap-1 items-center ">
                      <WechatIcon className="text-constructive" size={16} />
                      <span>{o.label}</span>
                      <Tip className="cursor-help" label={o.tip}><AlertCircleIcon size={12} /></Tip>
                    </div>
                  </label>
                )
              }
              return null;
            })
          }
        </div>
        <DialogFooter className="flex flex-row justify-end">
          <Button
            variant="outline"
            size="sm"
            className="px-3 min-w-[80px]"
            onClick={() => {
              updateOpen(false);
            }}
          >
            {presentation?.cancelLabel || '取消'}
          </Button>
          <Button
            disabled={loading || buttonDisabled}
            size="sm"
            className="px-3 min-w-[120px]"
            onClick={async () => {
              try {
                setLoading(true);
                toast.loading(presentation?.loadingMessage || '反馈提交中', {
                  id: 'sumbit'
                });
                const { data }: {
                  data: {
                    message: string;
                    code: number;
                    data: Object;
                  },
                } = await fun.post(
                  api('/common/feedback', true),
                  buildFeedbackRequestPayload(
                    transferCamelToSnake(scene),
                    content,
                    transformSubmitContent,
                  ),
                );
                toast.dismiss('sumbit');
                if (data.code === 0) {
                  updateOpen(false);
                  toast.success(presentation?.successMessage || '反馈已提交');
                } else {
                  toast.error(presentation?.failureMessage || data.message);
                }
              } catch {
                toast.dismiss('sumbit');
                toast.error(presentation?.failureMessage || '反馈提交失败，请稍后重试');
              } finally {
                setLoading(false);
              }
            }}
          >
            {presentation?.submitLabel || '提交'}
          </Button>
        </DialogFooter>
      </DialogContent> : null}
    </Dialog>
  )
}
